import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaChevronLeft, FaChevronRight, FaRegCalendarAlt } from "react-icons/fa";

/**
 * Calendario per scegliere arrivo e partenza, scritto in casa: niente
 * librerie e nessun calendario di sistema, che cambia faccia a ogni browser.
 *
 * Primo clic: arrivo. Secondo clic: partenza. Un clic su una data prima
 * dell'arrivo ricomincia da capo.
 */

const GIORNO = 86400000;

const aStringa = (d) => d.toISOString().slice(0, 10);
const daStringa = (s) => (s ? new Date(`${s}T00:00:00Z`) : null);
const oggiUTC = () => {
  const d = new Date();
  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
};

/** Le celle di un mese, con i buchi iniziali per far tornare i lunedi'. */
function celle(anno, mese) {
  const primo = new Date(Date.UTC(anno, mese, 1));
  const giorniNelMese = new Date(Date.UTC(anno, mese + 1, 0)).getUTCDate();
  // getUTCDay(): domenica e' 0, ma la settimana qui parte di lunedi'.
  const spostamento = (primo.getUTCDay() + 6) % 7;

  const risultato = new Array(spostamento).fill(null);
  for (let g = 1; g <= giorniNelMese; g += 1) {
    risultato.push(new Date(Date.UTC(anno, mese, g)));
  }
  return risultato;
}

function Mese({ anno, mese, dal, al, hover, minimo, onScegli, onHover, lingua }) {
  const giorni = useMemo(() => celle(anno, mese), [anno, mese]);

  const titolo = new Intl.DateTimeFormat(lingua || "it", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(anno, mese, 1)));

  const iniziali = useMemo(() => {
    const f = new Intl.DateTimeFormat(lingua || "it", {
      weekday: "short",
      timeZone: "UTC",
    });
    // 5 gennaio 2026 e' un lunedi'.
    return [0, 1, 2, 3, 4, 5, 6].map((i) =>
      f.format(new Date(Date.UTC(2026, 0, 5 + i))).slice(0, 2)
    );
  }, [lingua]);

  const dalD = daStringa(dal);
  const alD = daStringa(al);
  const fine = alD || (dal && hover && hover > dalD ? hover : null);

  return (
    <div className="w-full">
      <div className="text-center text-[#f5f1e8] font-semibold capitalize mb-3">
        {titolo}
      </div>

      <div className="grid grid-cols-7 gap-y-1 text-center">
        {iniziali.map((g, i) => (
          <div
            key={`${g}-${i}`}
            className="text-[10px] uppercase tracking-wider text-[#c9a962] pb-1"
          >
            {g}
          </div>
        ))}

        {giorni.map((giorno, i) => {
          if (!giorno) return <div key={`vuoto-${i}`} />;

          const chiave = aStringa(giorno);
          const passato = giorno < minimo;
          const isDal = dal === chiave;
          const isAl = al === chiave;
          const dentro =
            dalD && fine && giorno > dalD && giorno < fine && !passato;

          const estremo = isDal || isAl;

          return (
            <button
              key={chiave}
              type="button"
              disabled={passato}
              onClick={() => onScegli(chiave)}
              onMouseEnter={() => onHover(giorno)}
              className={[
                "relative h-9 text-sm transition-colors",
                passato
                  ? "text-[#4a453b] cursor-not-allowed"
                  : "text-[#f5f1e8] hover:bg-[#2a2418] cursor-pointer",
                dentro ? "bg-[#c9a962]/20" : "",
                estremo ? "bg-lux-accent text-[#161310] font-semibold hover:bg-lux-accent" : "",
              ].join(" ")}
            >
              {giorno.getUTCDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function CalendarioDate({ dal, al, onCambia, className = "" }) {
  const { t, i18n } = useTranslation();
  const [aperto, setAperto] = useState(false);
  const [hover, setHover] = useState(null);
  const contenitore = useRef(null);

  const minimo = useMemo(() => oggiUTC(), []);
  const partenza = daStringa(dal) || minimo;
  const [primoMese, setPrimoMese] = useState({
    anno: partenza.getUTCFullYear(),
    mese: partenza.getUTCMonth(),
  });

  // Chiusura cliccando fuori: un pannello che resta aperto da' fastidio.
  useEffect(() => {
    if (!aperto) return undefined;
    const fuori = (e) => {
      if (contenitore.current && !contenitore.current.contains(e.target)) {
        setAperto(false);
      }
    };
    document.addEventListener("mousedown", fuori);
    return () => document.removeEventListener("mousedown", fuori);
  }, [aperto]);

  const scegli = (chiave) => {
    const scelta = daStringa(chiave);
    const dalD = daStringa(dal);

    // Nessun arrivo, oppure intervallo gia' completo, oppure data precedente:
    // in tutti e tre i casi si ricomincia da questa data.
    if (!dal || al || scelta <= dalD) {
      onCambia({ dal: chiave, al: "" });
      return;
    }

    onCambia({ dal, al: chiave });
    setAperto(false);
  };

  const muovi = (passo) => {
    setPrimoMese((m) => {
      const d = new Date(Date.UTC(m.anno, m.mese + passo, 1));
      return { anno: d.getUTCFullYear(), mese: d.getUTCMonth() };
    });
  };

  const secondoMese = useMemo(() => {
    const d = new Date(Date.UTC(primoMese.anno, primoMese.mese + 1, 1));
    return { anno: d.getUTCFullYear(), mese: d.getUTCMonth() };
  }, [primoMese]);

  const etichetta = (valore, vuoto) => {
    const d = daStringa(valore);
    if (!d) return vuoto;
    return new Intl.DateTimeFormat(i18n.language || "it", {
      weekday: "short",
      day: "numeric",
      month: "short",
      timeZone: "UTC",
    }).format(d);
  };

  const notti =
    dal && al ? Math.round((daStringa(al) - daStringa(dal)) / GIORNO) : null;

  return (
    <div ref={contenitore} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setAperto((v) => !v)}
        className="w-full flex items-stretch text-left bg-[#14120f]"
      >
        <span className="flex-1 px-5 py-3 border-r border-[#2e2921] flex items-center gap-3">
          <FaRegCalendarAlt className="text-lux-accent w-4 h-4 shrink-0" />
          <span>
            <span className="block text-[10px] tracking-widest uppercase text-[#c9a962]">
              {t("ricerca.arrivo", "Arrivo")}
            </span>
            <span className="block text-base font-semibold capitalize text-[#f5f1e8] whitespace-nowrap">
              {etichetta(dal, t("ricerca.scegliData", "Scegli"))}
            </span>
          </span>
        </span>

        <span className="flex-1 px-5 py-3 flex items-center gap-3">
          <FaRegCalendarAlt className="text-lux-accent w-4 h-4 shrink-0" />
          <span>
            <span className="block text-[10px] tracking-widest uppercase text-[#c9a962]">
              {t("ricerca.partenza", "Partenza")}
            </span>
            <span className="block text-base font-semibold capitalize text-[#f5f1e8] whitespace-nowrap">
              {etichetta(al, t("ricerca.scegliData", "Scegli"))}
            </span>
          </span>
        </span>
      </button>

      {aperto && (
        <div
          className="absolute z-50 mt-2 left-0 bg-[#14120f] shadow-2xl border border-[#2e2921] p-5 w-[min(92vw,640px)]"
          onMouseLeave={() => setHover(null)}
        >
          <div className="flex items-center justify-between mb-3">
            <button
              type="button"
              onClick={() => muovi(-1)}
              className="p-2 text-[#c9a962] hover:bg-[#2a2418] transition-colors"
              aria-label={t("ricerca.mesePrecedente", "Mese precedente")}
            >
              <FaChevronLeft />
            </button>
            <span className="text-[11px] uppercase tracking-widest text-[#c9a962]">
              {notti
                ? `${notti} ${
                    notti === 1
                      ? t("ricerca.notte", "notte")
                      : t("ricerca.notti", "notti")
                  }`
                : t("ricerca.scegliArrivo", "Scegli la data di arrivo")}
            </span>
            <button
              type="button"
              onClick={() => muovi(1)}
              className="p-2 text-[#c9a962] hover:bg-[#2a2418] transition-colors"
              aria-label={t("ricerca.meseSuccessivo", "Mese successivo")}
            >
              <FaChevronRight />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Mese
              {...primoMese}
              dal={dal}
              al={al}
              hover={hover}
              minimo={minimo}
              onScegli={scegli}
              onHover={setHover}
              lingua={i18n.language}
            />
            <div className="hidden md:block">
              <Mese
                {...secondoMese}
                dal={dal}
                al={al}
                hover={hover}
                minimo={minimo}
                onScegli={scegli}
                onHover={setHover}
                lingua={i18n.language}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
